import React, { useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import Home from '@rover/icons/svg/home.svg';
import { Text } from '@rover/kibble/core';
import Row from '@rover/kibble/patterns/Row';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import useHomeAddress from '../hooks/useHomeAddress';
import { AddressType } from '../LocationInput.constants';
import { createAddressOptionRowWebProps } from '../utils';

import AddressModal from './AddressForm/AddressModal';
import EditButton from './EditButton';
import OptionLabel from './OptionLabel';

type Props = {
  formattedAddress?: string;
  onEdit: (newAddressData: UserInformationAddress) => void;
  forceUseHomeAddress?: boolean;
  blurInput?: () => void;
  onSelectAddress?: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

export default function HomeAddressOption({
  formattedAddress,
  onEdit,
  forceUseHomeAddress,
  blurInput,
  onSelectAddress,
}: Props): JSX.Element {
  const { t } = useLingui();
  const { address } = useHomeAddress(forceUseHomeAddress);
  const [isHomeAddressUpdateModalOpen, setIsHomeAddressUpdateModalOpen] = useState(false);

  const onClickEdit = (e): void => {
    e.stopPropagation();
    emitMetric('search-edit-home-address-click', { source: 'web' });
    fireDataLayerEvent({ event: 'search-edit-home-address-click' });
    setIsHomeAddressUpdateModalOpen(true);
  };

  const onCloseModal = (): void => {
    setIsHomeAddressUpdateModalOpen(false);
    blurInput?.();
  };

  return (
    <OptionLabel title={t`Home address`}>
      <Row
        onClick={() => {
          const event = { preventDefault: () => {} } as React.MouseEvent;
          onSelectAddress?.(event);
        }}
        ariaLabel={t`Home address`}
        label={t`Home address`}
        sublabel={
          <Text
            size="100"
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
            textColor="tertiary"
          >
            {formattedAddress || t`None`}
          </Text>
        }
        leftItem={<Home width={24} height={24} />}
        rightItem={<EditButton onClick={onClickEdit} onBlur={blurInput} />}
        webProps={createAddressOptionRowWebProps((e) => onSelectAddress?.(e), blurInput)}
      />
      <AddressModal
        isOpen={isHomeAddressUpdateModalOpen}
        onClose={onCloseModal}
        isEditMode
        addressType={AddressType.Home}
        address={address}
        onEdit={onEdit}
      />
    </OptionLabel>
  );
}
